import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUpRight, Pause, Play, Radar, ShieldCheck, Target } from "lucide-react";

const MODES = [
  {
    id: "defense", version: "V1", name: "Defense", icon: ShieldCheck,
    label: "Risiken erkennen.", caption: "Downside, Fragilität und Kapitalgrenzen.",
    detail: "Risiko bildet den Rahmen. V1 ordnet ein, was gegen eine Position spricht.",
    color: "#78e9e2", rgb: [120, 233, 226],
  },
  {
    id: "decision", version: "V2", name: "Decision", icon: Target,
    label: "Richtung verstehen.", caption: "Richtung, Erwartung und Entscheidungsevidenz.",
    detail: "V2 liefert Schätzungen. Ihr Wert zeigt sich erst im Vergleich mit späteren Ergebnissen.",
    color: "#87b4ff", rgb: [135, 180, 255],
  },
  {
    id: "offense", version: "V3", name: "Offense", icon: Radar,
    label: "Chancen erforschen.", caption: "Alpha, Dynamik und asymmetrisches Upside.",
    detail: "V3 untersucht außergewöhnliche Bewegungen im Shadow-Modus. Keine eigene Kaufzulassung.",
    color: "#c8a1ff", rgb: [200, 161, 255],
  },
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return reduced;
}

/* Decorative, deterministic geometry. Never market data or telemetry. */
function SignalSculpture({ active, animate }) {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const clockRef = useRef(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return undefined;
    const context = canvas.getContext("2d");
    if (!context) return undefined;
    let width = 1;
    let height = 1;
    let frame = 0;
    let elapsed = clockRef.current;
    let previous = 0;
    let lastPaint = 0;
    let visible = true;
    let pointerX = 0;
    let pointerY = 0;
    let easedX = 0;
    let easedY = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const strands = MODES.flatMap((mode, lane) =>
      Array.from({ length: 5 }, (_, ribbon) => ({
        lane,
        rgb: mode.rgb,
        ribbon,
        points: Array.from({ length: 121 }, (_, index) => {
          const angle = index / 120 * Math.PI * 2;
          const phase = lane / 3 * Math.PI * 2;
          const tube = .44 + ribbon * .033;
          const radius = 1.88 + tube * Math.cos(3 * angle + phase);
          return {
            x: radius * Math.cos(2 * angle),
            y: radius * Math.sin(2 * angle),
            z: tube * Math.sin(3 * angle + phase) + (ribbon - 2) * .035,
          };
        }),
      })),
    );

    function project(point, transform) {
      const x = point.x * transform.cosR + point.z * transform.sinR;
      const depth = -point.x * transform.sinR + point.z * transform.cosR;
      const y = point.y * transform.cosT - depth * transform.sinT;
      const z = point.y * transform.sinT + depth * transform.cosT;
      const perspective = 6.5 / (6.5 + z);
      const scale = Math.min(width * .17, height * .18);
      return { x: width * .51 + x * scale * perspective, y: height * .49 + y * scale * perspective, z };
    }
    function paint() {
      context.clearRect(0, 0, width, height);
      const time = elapsed / 1000;
      const rotation = -.42 + time * .065 + easedX * .16;
      const tilt = .85 + easedY * .14 + Math.sin(time * .16) * .07;
      const transform = { cosR: Math.cos(rotation), sinR: Math.sin(rotation), cosT: Math.cos(tilt), sinT: Math.sin(tilt) };
      const glow = context.createRadialGradient(width * .51, height * .49, 0, width * .51, height * .49, Math.min(width, height) * .46);
      glow.addColorStop(0, `rgba(${MODES[active].rgb.join(",")},.09)`);
      glow.addColorStop(1, "rgba(0,0,0,0)");
      context.fillStyle = glow;
      context.fillRect(0, 0, width, height);
      const paths = strands.map(strand => ({ ...strand, projected: strand.points.map(p => project(p, transform)) }));
      paths.sort((a, b) => b.projected[0].z - a.projected[0].z);
      for (const strand of paths) {
        const selected = strand.lane === active;
        const alpha = selected ? .44 + strand.ribbon * .1 : .12;
        const rgb = strand.rgb.join(",");
        context.beginPath();
        strand.projected.forEach((p, index) => index ? context.lineTo(p.x, p.y) : context.moveTo(p.x, p.y));
        context.strokeStyle = `rgba(${rgb},${alpha})`;
        context.lineWidth = selected ? 1.1 : .65;
        context.shadowBlur = selected && strand.ribbon === 2 ? 13 : 0;
        context.shadowColor = `rgba(${rgb},.5)`;
        context.stroke();
        context.shadowBlur = 0;
        if (strand.ribbon !== 2) continue;
        for (let i = 0; i < 10; i += 1) {
          const index = Math.floor((i * 12 + time * (selected ? 4 : 2)) % 120);
          const p = strand.projected[index];
          context.beginPath();
          context.arc(p.x, p.y, selected ? 2 : 1.1, 0, Math.PI * 2);
          context.fillStyle = `rgba(${rgb},${selected ? .95 : .3})`;
          context.fill();
        }
      }
    }
    function tick(now) {
      frame = 0;
      if (!animate || !visible || document.hidden) return;
      if (previous) elapsed += Math.min(now - previous, 70);
      previous = now;
      if (now - lastPaint >= 1000 / 30) {
        easedX += (pointerX - easedX) * .07;
        easedY += (pointerY - easedY) * .07;
        paint();
        lastPaint = now;
      }
      frame = requestAnimationFrame(tick);
    }
    function resume() {
      if (!animate || !visible || document.hidden || frame) return;
      previous = 0;
      frame = requestAnimationFrame(tick);
    }
    function stop() {
      cancelAnimationFrame(frame);
      frame = 0;
      previous = 0;
    }
    function resize() {
      const bounds = stage.getBoundingClientRect();
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      paint();
    }
    function move(event) {
      if (!animate || event.pointerType === "touch") return;
      const bounds = stage.getBoundingClientRect();
      pointerX = ((event.clientX - bounds.left) / Math.max(1, bounds.width) - .5) * 2;
      pointerY = ((event.clientY - bounds.top) / Math.max(1, bounds.height) - .5) * 2;
    }
    function resetPointer() { pointerX = 0; pointerY = 0; }
    function visibility() { if (document.hidden) stop(); else resume(); }
    const resizer = new ResizeObserver(resize);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) resume(); else stop();
    }, { threshold: .05 });
    resizer.observe(stage);
    observer.observe(stage);
    stage.addEventListener("pointermove", move, { passive: true });
    stage.addEventListener("pointerleave", resetPointer);
    document.addEventListener("visibilitychange", visibility);
    resize();
    resume();
    return () => {
      stop();
      clockRef.current = elapsed;
      resizer.disconnect();
      observer.disconnect();
      stage.removeEventListener("pointermove", move);
      stage.removeEventListener("pointerleave", resetPointer);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [active, animate]);

  return (
    <div className="hp-sculpture" ref={stageRef} aria-hidden="true">
      <div className="hp-sculpture-grid" />
      <svg className="hp-orbital-guides" viewBox="0 0 700 700" fill="none">
        <circle cx="350" cy="350" r="272" stroke="currentColor" strokeDasharray="2 12" />
        <ellipse cx="350" cy="350" rx="307" ry="148" stroke="currentColor" transform="rotate(-28 350 350)" />
        <path d="M350 30v35m0 570v35M30 350h35m570 0h35" stroke="currentColor" />
      </svg>
      <canvas ref={canvasRef} />
      <div className="hp-core-word">HQS<span>RESEARCH INTELLIGENCE</span></div>
      <span className="hp-coordinate hp-coordinate-a">DATA → CONTEXT</span>
      <span className="hp-coordinate hp-coordinate-b">MODEL → EVIDENCE</span>
    </div>
  );
}

export default function PublicSignalStage() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useReducedMotion();
  const selected = MODES[active];
  const animate = !paused && !reducedMotion;
  const Icon = selected.icon;
  return (
    <section id="top" className="hp-hero" style={{ "--signal-accent": selected.color }}>
      <div className="hp-hero-atmosphere" aria-hidden="true" />
      <div className="hq-container hp-hero-layout">
        <div className="hp-hero-copy">
          <p className="hp-kicker"><span /> THE HQS PERSPECTIVE <i>01 — 03</i></p>
          <h1>Hinter jedem<br />Signal liegt<br /><em>mehr.</em></h1>
          <p className="hp-hero-lead">Risiko. Richtung. Chance. HQS verbindet drei Perspektiven auf den Markt zu einer neuen Tiefe von Research.</p>
          <div className="hq-actions">
            <a href="#technologie" className="hp-primary-link">Intelligenz entdecken <ArrowUpRight size={20} aria-hidden="true" /></a>
            <a href="#system" className="hp-secondary-link">Die Plattform erleben <ArrowRightIcon /></a>
          </div>
          <p className="hp-hero-context">Quantitative Modelle · KI-Analyse · Paper-Portfolios</p>
        </div>
        <figure className="hp-hero-art" aria-label="Abstrakte Darstellung der drei HQS-Modellperspektiven">
          <div className="hp-art-head"><span>HQS / SIGNAL ARCHITECTURE</span><span>CONCEPT VISUAL</span></div>
          <SignalSculpture active={active} animate={animate} />
          <figcaption className="hp-art-caption">
            <span>Architekturvisualisierung · keine Marktdaten</span>
            <button type="button" className="hp-motion-toggle" onClick={() => setPaused(value => !value)} disabled={reducedMotion} aria-label={reducedMotion ? "Bewegung durch Geräteeinstellung reduziert" : paused ? "Animation abspielen" : "Animation pausieren"}>
              {animate ? <Pause size={13} aria-hidden="true" /> : <Play size={13} aria-hidden="true" />}
              {reducedMotion ? "REDUZIERTE BEWEGUNG" : paused ? "ABSPIELEN" : "PAUSIEREN"}
            </button>
          </figcaption>
        </figure>
        <div className="hp-perspectives">
          <div className="hp-perspective-heading"><span>PERSPEKTIVE WÄHLEN</span><span>01 / INTERAKTIV</span></div>
          <div className="hp-mode-buttons" role="group" aria-label="Visualisierte Modellperspektive">
            {MODES.map((mode, index) => (
              <button type="button" key={mode.id} aria-pressed={active === index} aria-controls="hp-perspective-description" onClick={() => setActive(index)} style={{ "--mode-color": mode.color }}>
                <span>{mode.version}</span><strong>{mode.name}</strong><ArrowUpRight size={17} aria-hidden="true" />
              </button>
            ))}
          </div>
          <div id="hp-perspective-description" className="hp-perspective-description" aria-live="polite" aria-atomic="true">
            <Icon size={20} strokeWidth={1.4} aria-hidden="true" />
            <div><strong>{selected.label}</strong><span>{selected.caption}</span></div>
            <p>{selected.detail}</p>
          </div>
        </div>
      </div>
      <div className="hq-container hp-hero-bottom"><span>INDEPENDENT THINKING. CONNECTED INTELLIGENCE.</span><a href="#plattform">Weiter entdecken <ArrowDown size={15} aria-hidden="true" /></a></div>
    </section>
  );
}

function ArrowRightIcon() {
  return <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M3 9h12M10 4l5 5-5 5" stroke="currentColor" strokeWidth="1.4" /></svg>;
}
